import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { NumberField } from "@/components/common";
import { DialogFooter } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toDisplay } from "@/lib/helpers";
import * as Icons from "lucide-react";

const EMPTY = {
  reference: "", name: "", category: "", category_custom: "", client_id: "", site_id: "",
  zone: "", location_desc: "", manufacturer: "", model: "", serial: "", install_date: "",
  installer: "", support_nature: "", fixation_mode: "", mode: "individuel", quantity: null,
  unit: "U", next_check_date: "", internal_notes: "",
};

export function CareEquipmentForm({ initial, meta, lockContext, onSave, onCancel, testPrefix = "eq" }) {
  const [f, setF] = useState({ ...EMPTY, ...(initial || {}) });
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setV = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)); }, []);
  const loadSites = useCallback(() => {
    if (f.client_id) api.get("/sites", { params: { client_id: f.client_id } }).then((r) => setSites(r.data));
    else setSites([]);
  }, [f.client_id]);
  useEffect(() => { loadSites(); }, [loadSites]);

  const valid = f.reference.trim() && f.name.trim() && f.category && f.client_id && f.site_id
    && (f.mode !== "ensemble" || (f.unit && f.quantity != null));

  const submit = async () => {
    setSaving(true);
    const payload = {
      ...f,
      reference: f.reference.trim(), name: f.name.trim(),
      install_date: f.install_date || null,
      next_check_date: f.next_check_date || null,
      quantity: f.mode === "ensemble" ? f.quantity : (f.quantity ?? null),
      unit: f.mode === "ensemble" ? f.unit : null,
      category_custom: f.category === "autre" ? (f.category_custom || null) : null,
    };
    try { await onSave(payload); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Référence lisible * <span className="text-xs font-normal text-slate-400">(unique par site)</span></Label>
          <Input value={f.reference} onChange={set("reference")} placeholder="Ex. PLOT-012" data-testid={`${testPrefix}-reference`} /></div>
        <div className="space-y-1.5"><Label>Nom *</Label>
          <Input value={f.name} onChange={set("name")} placeholder="Ex. Plot solaire allée" data-testid={`${testPrefix}-name`} /></div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Catégorie *</Label>
          <Select value={f.category} onValueChange={(v) => setV("category", v)}>
            <SelectTrigger data-testid={`${testPrefix}-category`}><SelectValue placeholder="Choisir" /></SelectTrigger>
            <SelectContent>{Object.entries(meta?.categories || {}).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select></div>
        {f.category === "autre" && (
          <div className="space-y-1.5"><Label>Préciser la catégorie</Label>
            <Input value={f.category_custom || ""} onChange={set("category_custom")} data-testid={`${testPrefix}-category-custom`} /></div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Client *</Label>
          <Select value={f.client_id} onValueChange={(v) => { setV("client_id", v); setV("site_id", ""); }} disabled={lockContext}>
            <SelectTrigger data-testid={`${testPrefix}-client`}><SelectValue placeholder="Choisir un client" /></SelectTrigger>
            <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="space-y-1.5"><Label>Site *</Label>
          <Select value={f.site_id} onValueChange={(v) => setV("site_id", v)} disabled={lockContext || !f.client_id}>
            <SelectTrigger data-testid={`${testPrefix}-site`}><SelectValue placeholder={f.client_id ? "Choisir un site" : "Sélectionnez un client"} /></SelectTrigger>
            <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select></div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Zone</Label>
          <Input value={f.zone} onChange={set("zone")} placeholder="Ex. Escalier Est" data-testid={`${testPrefix}-zone`} /></div>
        <div className="space-y-1.5"><Label>Emplacement précis</Label>
          <Input value={f.location_desc} onChange={set("location_desc")} data-testid={`${testPrefix}-location`} /></div>
      </div>

      {/* Mode individuel / ensemble */}
      <div className="space-y-1.5">
        <Label>Type de fiche</Label>
        <div className="grid grid-cols-2 gap-2">
          {[{ v: "individuel", l: "Équipement individuel" }, { v: "ensemble", l: "Ensemble suivi" }].map((o) => (
            <button key={o.v} type="button" onClick={() => setV("mode", o.v)} data-testid={`${testPrefix}-mode-${o.v}`}
              className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${f.mode === o.v ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600"}`}>
              {o.l}
            </button>
          ))}
        </div>
        {f.mode === "ensemble" && (
          <div className="mt-2 grid grid-cols-2 gap-3 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
            <NumberField label="Quantité" value={f.quantity} onChange={(v) => setV("quantity", v)} testId={`${testPrefix}-quantity`} />
            <div className="space-y-1.5"><Label className="text-xs text-slate-600">Unité</Label>
              <Select value={f.unit} onValueChange={(v) => setV("unit", v)}>
                <SelectTrigger data-testid={`${testPrefix}-unit`}><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(meta?.units || {}).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
            <p className="col-span-2 text-xs text-slate-500">Le nombre de fiches n'est pas le nombre physique d'équipements ; les unités différentes ne sont jamais additionnées.</p>
          </div>
        )}
      </div>

      <details className="rounded-lg border border-slate-200 p-3">
        <summary className="cursor-pointer text-sm font-medium text-slate-700">Informations complémentaires (facultatives)</summary>
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="space-y-1.5"><Label>Fabricant</Label><Input value={f.manufacturer} onChange={set("manufacturer")} data-testid={`${testPrefix}-manufacturer`} /></div>
            <div className="space-y-1.5"><Label>Modèle</Label><Input value={f.model} onChange={set("model")} data-testid={`${testPrefix}-model`} /></div>
            <div className="space-y-1.5"><Label>N° de série</Label><Input value={f.serial} onChange={set("serial")} data-testid={`${testPrefix}-serial`} /></div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Date de pose</Label><Input type="date" value={f.install_date || ""} onChange={set("install_date")} data-testid={`${testPrefix}-install-date`} /></div>
            <div className="space-y-1.5"><Label>Entreprise de pose</Label><Input value={f.installer} onChange={set("installer")} data-testid={`${testPrefix}-installer`} /></div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Nature du support</Label><Input value={f.support_nature} onChange={set("support_nature")} data-testid={`${testPrefix}-support`} /></div>
            <div className="space-y-1.5"><Label>Mode de fixation</Label><Input value={f.fixation_mode} onChange={set("fixation_mode")} data-testid={`${testPrefix}-fixation`} /></div>
          </div>
          <div className="space-y-1.5"><Label>Prochaine date de contrôle (facultatif)</Label>
            <Input type="date" value={f.next_check_date || ""} onChange={set("next_check_date")} data-testid={`${testPrefix}-next-check`} />
            <p className="text-xs text-slate-400">Échéance choisie librement — aucune fréquence réglementaire n'est imposée.</p></div>
          <div className="space-y-1.5"><Label>Notes internes</Label><Textarea value={f.internal_notes} onChange={set("internal_notes")} data-testid={`${testPrefix}-notes`} /></div>
        </div>
      </details>

      <DialogFooter>
        {onCancel && <Button variant="outline" onClick={onCancel} data-testid={`${testPrefix}-cancel`}>Annuler</Button>}
        <Button onClick={submit} disabled={!valid || saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`${testPrefix}-save`}>
          {saving ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
        </Button>
      </DialogFooter>
    </div>
  );
}
